/**
 * Add an appointment, or change one.
 *
 * An appointment with nobody picked is shared with everyone, which is the opposite of
 * what an empty list usually means, so the form says so under the picker rather than
 * leaving it to be discovered. For anyone who is not office staff the picker is absent
 * altogether: the server makes their appointment theirs alone, and offering a choice
 * that is ignored would be a lie.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Combobox } from '@/components/Combobox';
import { useConfirm } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { Field } from '@/components/ui';
import { jobOptions, PeopleMultiSelect, siteOptions } from '@/features/access/people';
import { usePermissions } from '@/features/access/usePermissions';
import { useI18n } from '@/i18n';
import { api, ApiError, type CalendarEventInput, type CalendarItem } from '@/lib/api';
import { fromMilan, isoDate, toMilanInput } from '@/lib/format';

interface Form {
  title: string;
  place: string;
  description: string;
  allDay: boolean;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  siteId: string;
  jobId: string;
  attendeeIds: string[];
}

const blank = (day: string): Form => ({
  title: '',
  place: '',
  description: '',
  allDay: false,
  startDate: day,
  startTime: '09:00',
  endDate: '',
  endTime: '',
  siteId: '',
  jobId: '',
  attendeeIds: [],
});

function fromItem(item: CalendarItem): Form {
  const base = {
    title: item.title,
    place: item.place ?? '',
    description: item.description ?? '',
    allDay: item.allDay,
    siteId: item.siteId ?? '',
    jobId: item.jobId ?? '',
    attendeeIds: (item.attendees ?? []).map((a) => a.id),
  };
  const start = toMilanInput(item.start);
  const end = item.end ? toMilanInput(item.end) : '';

  if (item.allDay) {
    const startDate = isoDate(item.start);
    const endDate = item.end ? isoDate(item.end) : '';
    return {
      ...base,
      startDate,
      startTime: '09:00',
      endDate: endDate && endDate !== startDate ? endDate : '',
      endTime: '',
    };
  }
  return {
    ...base,
    startDate: start.slice(0, 10),
    startTime: start.slice(11, 16),
    endDate: end && end.slice(0, 10) !== start.slice(0, 10) ? end.slice(0, 10) : '',
    endTime: end ? end.slice(11, 16) : '',
  };
}

/** What goes to the API, or the reason it cannot. Pure, so the rules read in one place. */
export function buildEvent(form: Form, isOffice: boolean): { body: CalendarEventInput } | { error: 'endBeforeStart' } {
  const startsAt = fromMilan(`${form.startDate}T${form.allDay ? '00:00' : form.startTime}:00`);

  let endsAt: string | null = null;
  if (form.allDay) {
    // The last day is inclusive, so the end is the final minute of it.
    if (form.endDate && form.endDate > form.startDate) endsAt = fromMilan(`${form.endDate}T23:59:00`);
    else if (form.endDate && form.endDate < form.startDate) return { error: 'endBeforeStart' };
  } else if (form.endTime) {
    endsAt = fromMilan(`${form.endDate || form.startDate}T${form.endTime}:00`);
    if (Date.parse(endsAt) < Date.parse(startsAt)) return { error: 'endBeforeStart' };
  }

  return {
    body: {
      title: form.title.trim(),
      description: form.description.trim() || null,
      place: form.place.trim() || null,
      startsAt,
      endsAt,
      allDay: form.allDay,
      siteId: form.siteId || null,
      jobId: form.jobId || null,
      attendeeIds: isOffice ? form.attendeeIds : [],
    },
  };
}

export function EventForm({
  open,
  onClose,
  event,
  day,
  isOffice,
}: {
  open: boolean;
  onClose: () => void;
  /** Present when editing. */
  event?: CalendarItem | null;
  /** The day a new appointment starts on. */
  day: string;
  isOffice: boolean;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { can } = usePermissions();
  const [form, setForm] = useState<Form>(blank(day));

  useEffect(() => {
    if (open) setForm(event ? fromItem(event) : blank(day));
  }, [open, event, day]);

  // The pickers come from sections the person may not have. A 403 there should leave the
  // field empty, not break the form, so each is fetched only when it could succeed.
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users(),
    enabled: open && isOffice && can('employees', 'read'),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites, enabled: open && can('sites', 'read') });
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: () => api.jobs(), enabled: open && can('jobs', 'read') });

  const people = useMemo(
    () => (users.data?.users ?? []).filter((u) => u.status === 'active' || u.status === 'invited'),
    [users.data],
  );

  const built = buildEvent(form, isOffice);
  const valid = form.title.trim().length > 0 && form.startDate !== '' && (form.allDay || form.startTime !== '');

  const finish = async () => {
    await queryClient.invalidateQueries({ queryKey: ['calendar'] });
    onClose();
  };

  const save = useMutation({
    mutationFn: async () => {
      if ('error' in built) throw new Error(t('calendar.endBeforeStart'));
      return event ? api.updateCalendarEvent(event.id, built.body) : api.createCalendarEvent(built.body);
    },
    onSuccess: finish,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteCalendarEvent(event!.id),
    onSuccess: finish,
  });

  const askRemove = async () => {
    const yes = await confirm({
      message: t('calendar.deleteConfirm', { title: event?.title ?? '' }),
      confirmLabel: t('calendar.deleteConfirmLabel'),
      danger: true,
    });
    if (yes) remove.mutate();
  };

  const error = save.error ?? remove.error;
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm({ ...form, [key]: value });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={event ? t('calendar.editTitle') : t('calendar.newTitle')}
      footer={
        <>
          <button
            type="button"
            className="btn-primary"
            disabled={!valid || save.isPending || remove.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {event ? (
            <button type="button" className="btn-danger" disabled={remove.isPending} onClick={() => void askRemove()}>
              {t('common.delete')}
            </button>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-danger ms-auto">
              {error instanceof ApiError || error instanceof Error ? error.message : t('common.saveError')}
            </p>
          ) : 'error' in built && valid ? (
            <p role="alert" className="text-sm text-danger ms-auto">
              {t('calendar.endBeforeStart')}
            </p>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Field label={`${t('calendar.fieldTitle')} *`}>
            <input
              className="input"
              autoFocus
              maxLength={160}
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
            />
          </Field>
        </div>

        <label className="sm:col-span-2 flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={form.allDay} onChange={(e) => set('allDay', e.target.checked)} />
          {t('calendar.allDay')}
        </label>

        <Field label={t('calendar.startDate')}>
          <input
            type="date"
            className="input"
            value={form.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
        </Field>
        {form.allDay ? (
          <Field label={t('calendar.endDate')}>
            <input
              type="date"
              className="input"
              min={form.startDate}
              value={form.endDate}
              onChange={(e) => set('endDate', e.target.value)}
            />
          </Field>
        ) : (
          <Field label={t('calendar.startTime')}>
            <input
              type="time"
              className="input"
              value={form.startTime}
              onChange={(e) => set('startTime', e.target.value)}
            />
          </Field>
        )}

        {form.allDay ? null : (
          <>
            <Field label={t('calendar.endDate')}>
              <input
                type="date"
                className="input"
                min={form.startDate}
                value={form.endDate}
                onChange={(e) => set('endDate', e.target.value)}
              />
            </Field>
            <Field label={t('calendar.endTime')}>
              <input
                type="time"
                className="input"
                value={form.endTime}
                onChange={(e) => set('endTime', e.target.value)}
              />
            </Field>
          </>
        )}

        <div className="sm:col-span-2">
          <Field label={t('calendar.place')}>
            <input
              className="input"
              maxLength={200}
              value={form.place}
              onChange={(e) => set('place', e.target.value)}
            />
          </Field>
        </div>

        {can('sites', 'read') ? (
          <Field label={t('calendar.site')}>
            <Combobox
              value={form.siteId}
              onChange={(siteId) => set('siteId', siteId)}
              options={[{ value: '', label: t('calendar.noSite') }, ...siteOptions(sites.data?.sites ?? [])]}
            />
          </Field>
        ) : null}
        {can('jobs', 'read') ? (
          <Field label={t('calendar.job')}>
            <Combobox
              value={form.jobId}
              onChange={(jobId) => set('jobId', jobId)}
              options={[{ value: '', label: t('calendar.noJob') }, ...jobOptions(jobs.data?.jobs ?? [])]}
            />
          </Field>
        ) : null}

        <div className="sm:col-span-2">
          <Field label={t('common.description')}>
            <textarea
              className="input h-24 py-2"
              maxLength={2000}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label={t('calendar.attendeesField')} group hint={isOffice ? t('calendar.attendeesHint') : t('calendar.attendeesSelf')}>
            {isOffice ? (
              <PeopleMultiSelect
                users={people}
                selected={form.attendeeIds}
                onChange={(attendeeIds) => set('attendeeIds', attendeeIds)}
              />
            ) : null}
          </Field>
        </div>
      </div>
    </Modal>
  );
}
